import React, { useState } from 'react';
import { UserData } from '../../contexts/AuthContext';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface Conversation {
  id: string;
  participants: string[];
  lastMessage: string;
  lastMessageTime: any;
  unreadCount?: number;
  otherUser?: UserData;
}

interface ConversationListProps {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  onSelectConversation: (conversation: Conversation) => void;
  loading: boolean;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  loading
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation();
  
  // Filtrar conversaciones según la búsqueda
  const filteredConversations = conversations.filter(conversation => 
    conversation.otherUser?.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conversation.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  // Formatear la hora del último mensaje de forma más amigable
  const formatLastMessageTime = (timestamp: any) => {
    if (!timestamp) return '';
    
    const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
    const now = new Date();
    const messageDate = new Date(date);
    
    // Si es hoy, mostrar solo la hora
    if (messageDate.toDateString() === now.toDateString()) {
      return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // Si es ayer, mostrar "Ayer"
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    }
    
    // Si es esta semana, mostrar el día de la semana
    const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const dayDiff = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24);
    if (dayDiff < 7) {
      return daysOfWeek[messageDate.getDay()];
    }
    
    // Para fechas más antiguas, mostrar la fecha
    return messageDate.toLocaleDateString();
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full p-3">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-domired-600"></div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col h-full">
        {/* Barra de búsqueda */}
        <div className="px-3 py-2 sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm placeholder-gray-500 bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-domired-500 focus:border-domired-500"
              placeholder={t('messages.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex flex-col justify-center items-center flex-grow p-3 text-center">
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {t('messages.noConversations')}
          </p>
          <p className="text-gray-600 dark:text-gray-400 text-xs mt-2">
            {t('messages.visitProfiles')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Barra de búsqueda */}
      <div className="px-3 py-2 sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm placeholder-gray-500 bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-domired-500 focus:border-domired-500"
            placeholder={t('messages.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      
      {/* Lista de conversaciones con scroll independiente */}
      <div className="overflow-y-auto flex-grow h-full">
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {filteredConversations.map((conversation) => (
          <li
            key={conversation.id}
            className={`py-3 px-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors duration-150 ${
              selectedConversation?.id === conversation.id ? 'bg-gray-50 dark:bg-gray-800' : ''
            }`}
            onClick={() => onSelectConversation(conversation)}
          >
            <div className="flex items-center">
              <div className="relative flex-shrink-0">
                <img
                  src={conversation.otherUser?.photoURL || '/default-avatar.png'}
                  alt={conversation.otherUser?.displayName || t('common.user')}
                  className="h-12 w-12 rounded-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = '/default-avatar.png';
                  }}
                />
                {/* Indicador de estado online (se puede implementar más adelante) */}
                {/* Comentado hasta implementar la propiedad isOnline en el tipo UserData */}
                {/* {conversation.otherUser?.isOnline && (
                  <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-400 ring-2 ring-white dark:ring-gray-800"></span>
                )} */}
              </div>
              <div className="ml-3 flex-grow min-w-0"> {/* min-w-0 para que el truncate funcione correctamente */}
                <div className="flex justify-between items-center">
                  <h3 className="font-medium truncate">
                    {conversation.otherUser?.displayName || t('common.user')}
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1 whitespace-nowrap">
                    {formatLastMessageTime(conversation.lastMessageTime)}
                  </span>
                </div>
                <div className="flex items-center mt-1">
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate flex-grow">
                    {conversation.lastMessage || t('messages.noMessages')}
                  </p>
                  {conversation.unreadCount && conversation.unreadCount > 0 && (
                    <span className="ml-1 bg-domired-600 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                      {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
        </ul>
      </div>
    </div>
  );
};

export default ConversationList;
